/* ==========================================================================
   Undefeated — accessibility remediation, ticket P2-6 (+ N-13)
   Sunrise Integration · WCAG 2.2 AA · 2026-09-01

   Mobile navigation: keyboard operability and focus management.

   Deploy as assets/undtd-a11y.js. Placement does not matter — head or
   body, with or without defer — because initialisation waits for the DOM
   (see the readyState guard at the bottom). theme_undtd.js sits in the
   <head> carrying `defer`, so it has already executed by DOMContentLoaded
   and this script always runs after it.

   It does not modify theme_undtd.js. It watches #mobile-dropdown's class
   attribute, so it reacts to the theme's own open/close regardless of how
   that is triggered, and closes the panel by clicking the theme's own
   button rather than duplicating the theme's class toggling.

   window.__undtdA11yNav reports what happened, for verification:
     'active'   — observer attached, panel found
     'no-panel' — #mobile-dropdown or #NavDropdown absent on this template

   ---------------------------------------------------------------------------
   WHAT WAS MEASURED ON THE LIVE SITE (2026-09-01)

   #mobile-dropdown computes to height:0; overflow:hidden — but
   display:block and visibility:visible. Its descendants therefore keep
   visibility:visible, non-zero rects and an offsetParent, which means all
   217 of them stay in the tab order WHILE THE MENU IS CLOSED.

   Verified with real key presses at 402px wide: one Tab past the "Site
   navigation" button lands on "Shop" inside the closed panel, and the next
   four Tabs walk to "Contact" — index 4 of 217 — every one of them clipped
   out of view, so no focus indicator is visible anywhere on screen.

   The same is true at 1050px, where #NavDropdown is display:none. Desktop
   keyboard users tab through 217 invisible stops they cannot even open.

   This is broader than the audit's framing of P2-6 (which described the
   OPEN state). Logged as N-13 and the reason this ships ahead of the rest.

   Open state, also measured: panel animates to 688px of a 749px viewport,
   so it obscures the page; aria-expanded flips correctly to "true";
   Escape does nothing; focus never enters the panel and is never restored.
   ========================================================================== */

(function () {
  'use strict';

  function init() {

  var PANEL_ID = 'mobile-dropdown';
  var BTN_ID = 'NavDropdown';
  var OPEN_CLASS = 'open';
  /* Everything painted above the drawer, so it stays reachable while open. */
  var VISIBLE_CHROME = '.header_content_container';

  var panel = document.getElementById(PANEL_ID);
  var btn = document.getElementById(BTN_ID);
  if (!panel || !btn) { window.__undtdA11yNav = 'no-panel'; return; }

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', 'summary',
    '[tabindex]:not([tabindex^="-"])', '[contenteditable="true"]'
  ].join(',');

  var supportsInert = 'inert' in HTMLElement.prototype;
  var wasOpen = null;   /* null = first pass, so we do not steal focus on load */

  function isOpen() {
    return panel.classList.contains(OPEN_CLASS);
  }

  function focusablesIn(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) {
        if (el.closest('[inert]')) return false;
        var cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none') return false;
        return !!el.offsetParent || cs.position === 'fixed';
      }
    );
  }

  /* ---- closed: out of the tab order ------------------------------------ */

  function deactivate() {
    if (supportsInert) {
      panel.inert = true;
    } else {
      /* CSS fallback lives in undtd-a11y.css:
         #mobile-dropdown.a11y-nav-closed{visibility:hidden}
         visibility:hidden is inherited, so descendants leave the tab order
         in every engine. inert has no visual effect, so the two paths look
         identical; only the fallback needs the stylesheet. */
      panel.classList.add('a11y-nav-closed');
    }
    panel.setAttribute('aria-hidden', 'true');
  }

  function activate() {
    if (supportsInert) {
      panel.inert = false;
    } else {
      panel.classList.remove('a11y-nav-closed');
    }
    panel.removeAttribute('aria-hidden');
  }

  /* ---- open: focus in, Escape out, obscured page unreachable ----------- */

  function onKeydown(e) {
    if (!isOpen()) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      /* Close through the theme's own handler so the animation, the
         aria-expanded flip and body.js-drawer-open-down all stay in sync. */
      btn.click();
      /* The observer restores focus to btn when the class drops. */
      return;
    }

    if (e.key !== 'Tab') return;

    /* The drawer covers 688px of a 749px viewport. Keeping focus inside the
       header chrome plus the panel means focus never lands on something the
       drawer is painted over — 2.4.11 — while everything still visible on
       screen stays reachable. */
    var chrome = document.querySelector(VISIBLE_CHROME);
    var loop = (chrome ? focusablesIn(chrome) : [btn]).concat(focusablesIn(panel));
    if (loop.indexOf(btn) === -1) loop.unshift(btn);
    if (!loop.length) return;

    var first = loop[0];
    var last = loop[loop.length - 1];
    var i = loop.indexOf(document.activeElement);

    if (i === -1) {
      /* Focus is outside the loop — an injected node, or the page behind the
         drawer. Pull it back to whichever end the user was heading for. */
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && i === 0) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && i === loop.length - 1) {
      e.preventDefault();
      first.focus();
    }
    /* Anywhere in the middle: let the browser move focus itself. */
  }

  function opened() {
    activate();
    var targets = focusablesIn(panel);
    if (targets.length) targets[0].focus();
  }

  function closed() {
    /* Restore focus BEFORE making the panel inert, or the browser drops
       focus to <body> and the user loses their place entirely. */
    if (panel.contains(document.activeElement)) btn.focus();
    deactivate();
  }

  function sync() {
    var open = isOpen();
    if (open === wasOpen) return;
    if (wasOpen === null) {
      /* First pass on load: set the closed state without touching focus. */
      if (open) { activate(); } else { deactivate(); }
    } else if (open) {
      opened();
    } else {
      closed();
    }
    wasOpen = open;
  }

  new MutationObserver(sync).observe(panel, {
    attributes: true,
    attributeFilter: ['class']
  });

  document.addEventListener('keydown', onKeydown, true);

  sync();
    window.__undtdA11yNav = 'active';
  }

  /* The panel lives in the <body>, so initialising must not race the parser.
     Shopify's script_tag filter emits a plain <script src> with no defer, and
     theme.liquid loads it from the <head> — so on a first pass the panel does
     not exist yet and every getElementById returns null. Waiting for
     DOMContentLoaded makes the script correct wherever the tag is placed. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* ============================================================================
   P1-2 — autoplaying looping videos (WCAG 2.2.2 Pause, Stop, Hide)

   Every looping video gets a visible, keyboard-operable control that stops it
   and resets it to its first frame, and prefers-reduced-motion is honoured —
   which CSS cannot do, because CSS cannot stop playback.

   REPLACES the previous P1-2 block in assets/undtd-a11y.js. Delete that block
   before pasting this one — do not append both. Pairs with the P1-2 block in
   assets/undtd-a11y.css, which needs NO changes for this revision: the control
   markup, class names and data attributes are all unchanged.

   ---------------------------------------------------------------------------
   2026-09-25 — EXTENDED from .content-card to every component carrying a video.

   Measured on the homepage: 5 videos across 3 components, of which only the 2
   in .content-card had controls. Collection and product templates have no
   videos and no .graphic-cover at all, so this is homepage-only today.

     .content-card     2 videos   335x418    link does NOT wrap the video
     .content-feature  2 videos   710x568    link DOES wrap the video
     .und-hero         1 video    1459x700   no link at all, full-bleed

   All five share snippets/graphic-cover.liquid (.graphic-cover, 10 on the page,
   5 with video). Targeting is nevertheless an explicit component map rather
   than .graphic-cover: each component needs its own title selector, and the
   map keeps the .content-card behaviour already verified under P1-2 identical.
   A new video component is one more line in COMPONENTS.

   The insertion logic below needed no change to cover all three. It inserts
   after the first direct-child <a> of the host, and in .content-feature — the
   one component where the link genuinely wraps the video — that <a>'s parent
   IS .content-feature, so the button still lands outside the link. The hero has
   no <a>, so it falls through to host.appendChild.

   CORRECTION to the previous note here, which said autoplay must be present in
   snippets/graphic-cover.liquid. It is not present on any of the five. Theme JS
   starts them on scroll-into-view and adds .is-playing then (N-17). The initial
   label therefore comes from video.paused and reconciles on the first 'play'
   event, so nothing here depends on the attribute.

   Per the P2-6 deploy note, script_tag emits no defer and theme.liquid loads
   this from the head, so it waits for DOMContentLoaded itself.
   window.__undtdA11yMotion reports 'active' once attached.
   ============================================================================ */
(function () {
  'use strict';

  /* One entry per component that can carry a video. Lookup is by nearest
     ancestor, so the order here does not matter. */
  var COMPONENTS = [
    { root: '.content-card',    title: '.content-card__title' },
    { root: '.content-feature', title: '.content-feature__title' },
    { root: '.und-hero',        title: '.und-hero__cover-title', firstTextNodeOnly: true }
  ];

  function componentSelector(suffix) {
    var parts = [];
    for (var i = 0; i < COMPONENTS.length; i++) parts.push(COMPONENTS[i].root + suffix);
    return parts.join(',');
  }

  var ROOT_SEL    = componentSelector('');
  var VIDEO_SEL   = componentSelector(' video');
  var DONE_ATTR   = 'data-undtd-motion';
  var INTENT_ATTR = 'data-undtd-intent';   /* 'play' or 'stop', set by a person */

  var ICON_STOP = '<svg class="undtd-motion-ico-stop" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
                  '<rect x="1.75" y="1.75" width="8.5" height="8.5" rx="1"/></svg>';
  var ICON_PLAY = '<svg class="undtd-motion-ico-play" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
                  '<path d="M2.5 1.2 10.5 6l-8 4.8z"/></svg>';

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }

  function componentFor(host) {
    var matches = host.matches || host.msMatchesSelector || host.webkitMatchesSelector;
    if (!matches) return null;
    for (var i = 0; i < COMPONENTS.length; i++) {
      if (matches.call(host, COMPONENTS[i].root)) return COMPONENTS[i];
    }
    return null;
  }

  /* The component's own title, not the video's aria-label: the aria-labels are
     long prose, card 2's still carries the "Undeafeated" typo (N-2), and N-3
     calls for removing them so the link is the single name source. Videos share
     grids, so an unqualified "Stop video" would name several controls alike. */
  function nameFor(host, comp) {
    var label = '';
    var t = comp && comp.title ? host.querySelector(comp.title) : null;

    if (t) {
      if (comp.firstTextNodeOnly) {
        /* .und-hero__cover-title is a headline and a call to action in one
           element, joined by <br>: "Undefeated Spring Release<br>Shop Now".
           Plain textContent would name the control "...ReleaseShop Now". */
        for (var i = 0; i < t.childNodes.length; i++) {
          var n = t.childNodes[i];
          if (n.nodeType === 3 && n.textContent.trim()) { label = n.textContent; break; }
        }
      }
      if (!label) label = t.textContent;
    }

    if (!label) {
      var a = host.querySelector('a[aria-label]');
      label = a && a.getAttribute('aria-label');
    }
    return label ? label.replace(/\s+/g, ' ').trim() : '';
  }

  /* The name says what activating the control will DO, and it says "Stop"
     rather than "Pause" because the video is reset rather than held mid-frame.
     Toggling by name is the clearer convention for media controls; mixing that
     with aria-pressed is an anti-pattern. */
  function setState(btn, stopped) {
    if (!btn) return;
    var verb = stopped ? 'Play' : 'Stop';
    var name = btn.getAttribute('data-undtd-name');
    btn.setAttribute('data-state', stopped ? 'stopped' : 'playing');
    btn.setAttribute('aria-label', name ? verb + ' video: ' + name : verb + ' background video');
  }

  function enhance(video) {
    if (video.getAttribute(DONE_ATTR)) return;

    var host = video.closest(ROOT_SEL);
    if (!host) return;
    var comp = componentFor(host);

    video.setAttribute(DONE_ATTR, 'done');
    host.classList.add('undtd-has-motion');

    var btn = document.createElement('button');
    btn.setAttribute('type', 'button');
    btn.className = 'undtd-motion-btn';
    btn.setAttribute('data-undtd-name', nameFor(host, comp));
    btn.innerHTML = ICON_STOP + ICON_PLAY;
    /* An autoplaying video may not have started at this exact instant, but it is
       about to. Labelling it "Stop" now avoids a flash of the wrong control, and
       the media listeners below correct it if playback never happens. On this
       theme no video carries the attribute, so this reads video.paused. */
    setState(btn, video.hasAttribute('autoplay') ? false : video.paused);

    /* Sibling of the graphic link, never a descendant: a button inside an <a> is
       nested interactive content and gives an unpredictable a11y tree. Placed
       straight after that link so it lands next to the thing it controls in the
       tab order — confirmed with real key presses on the live site.

       Injected rather than rendered in Liquid: a server-rendered button would be
       a visible control that does nothing if the script failed. NOTE the trade
       this version accepts, which the play-on-press version did not: if this
       script fails to load, the videos autoplay with no way to stop them and
       2.2.2 fails outright. That is an argument for keeping this file small,
       dependency-free, and early. */
    var graphicLink = null;
    for (var i = 0; i < host.children.length; i++) {
      if (host.children[i].tagName === 'A') { graphicLink = host.children[i]; break; }
    }
    if (graphicLink && graphicLink.insertAdjacentElement) {
      graphicLink.insertAdjacentElement('afterend', btn);
    } else {
      host.appendChild(btn);
    }

    /* play() is not a promise that it played. It can be refused outright, and it
       can also resolve while the element stays paused. Either way the label must
       not go on claiming the video is running. */
    function start() {
      video.setAttribute(INTENT_ATTR, 'play');
      setState(btn, false);
      var p;
      try { p = video.play(); } catch (e) {}
      if (p && p.then) {
        p.then(
          function () { if (video.paused) setState(btn, true); },
          function () { setState(btn, true); }
        );
      }
    }

    /* Stop, not pause: the video is reset to its first frame rather than left
       holding whatever frame it happened to reach, which reads as a stopped
       video instead of a stuck one.

       Deliberately NOT restoring the poster attribute's image. That looks
       possible — swap in a fresh element and it renders the poster — but it only
       works on a cold cache. Once the clip is buffered a fresh element reports
       readyState 4 immediately and paints frame 0 anyway, so the poster restore
       silently stops happening for exactly the visitors who have seen the video,
       which with autoplay is everyone. Measured on the live site. Resetting to
       frame 0 does the same job deterministically. */
    function stop() {
      video.setAttribute(INTENT_ATTR, 'stop');
      try {
        video.pause();
        video.currentTime = 0;
      } catch (e) {}
      setState(btn, true);
    }

    video.addEventListener('pause', function () {
      /* N-18 pauses videos that scroll out of view. That is not the person
         stopping it, so the control must go on saying "Stop": its state still
         describes what the person chose, and the N-18 block resumes from the
         same frame when the video comes back. */
      if (video.hasAttribute('data-undtd-offscreen')) return;
      setState(btn, true);
    });

    video.addEventListener('play', function () {
      var intent = video.getAttribute(INTENT_ATTR);
      /* An explicit stop outranks anything that tries to restart it. */
      if (intent === 'stop') {
        try { video.pause(); } catch (e) {}
        setState(btn, true);
        return;
      }
      /* Reduced motion suppresses playback nobody asked for. It does not
         override an explicit press: the setting is there to stop unrequested
         motion, not to take the control away from the person. */
      if (reduced() && intent !== 'play') {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
        setState(btn, true);
        return;
      }
      setState(btn, false);
    });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      /* Branch on what the control is telling the person, not on the element's
         paused flag: the two can disagree when playback was refused, and the
         button must do what its own label promises. */
      if (btn.getAttribute('data-state') === 'stopped') { start(); } else { stop(); }
    });

    /* Someone who has asked their OS for reduced motion should never have to go
       find the control. */
    if (reduced()) stop();

    host.__undtdStop = stop;
  }

  function scan() {
    var vids = document.querySelectorAll(VIDEO_SEL);
    for (var i = 0; i < vids.length; i++) enhance(vids[i]);
  }

  function init() {
    scan();

    if (mq) {
      var onChange = function () {
        /* Turning the setting ON stops everything. Turning it OFF does not start
           anything: motion resumes only on a press. */
        if (!reduced()) return;
        var hosts = document.querySelectorAll(ROOT_SEL);
        for (var i = 0; i < hosts.length; i++) {
          if (hosts[i].__undtdStop) hosts[i].__undtdStop();
        }
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    /* Sections re-render on theme-editor edits and on some AJAX paths, which
       drops the control. Same MutationObserver approach as P2-6. */
    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; scan(); }, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.__undtdA11yMotion = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());

/* ------------------------------------------------------------------
   N-27 — cart-drawer backdrop is an invisible, unannounced tab stop
   ------------------------------------------------------------------
   button.backdrop is injected at runtime by theme_undtd.js (it is NOT
   in the server HTML), sits as a direct child of <body> on every page,
   and covers the viewport at opacity 0.

   The theme already flips aria-hidden and aria-expanded with drawer
   state. What it never sets is tabindex -- and pointer-events:none
   blocks only the mouse. So while the drawer is closed the element is
   focusable AND aria-hidden: focus lands somewhere that announces
   nothing and paints no ring.

   The drawer has its own button.cart-drawer__close ("Close Cart
   Drawer"), so the backdrop can leave the tab order permanently and
   stay what it is -- a click target for mouse users.
------------------------------------------------------------------ */
(function () {
  var SELECTOR = 'button.backdrop';

  function seal() {
    var el = document.querySelector(SELECTOR);
    if (!el || el.getAttribute('tabindex') === '-1') return;
    el.setAttribute('tabindex', '-1');
  }

  function init() {
    seal();

    // theme_undtd.js may inject it after this runs, and may replace it
    // on an ajax cart update. The backdrop is a direct child of <body>,
    // so childList alone is enough -- no subtree walk, no disconnect.
    new MutationObserver(seal).observe(document.body, { childList: true });

    window.__undtdA11yBackdrop = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ------------------------------------------------------------------
   N-34 — cart drawer: Escape to close, and focus returned on close
   ------------------------------------------------------------------
   #cart-drawer is role="dialog" aria-label="Cart Drawer". Measured with
   REAL key presses on the deployed theme, not synthetic events:

     - Escape did nothing. The drawer stayed .is-open and <body> kept
       cart-drawer-open; only a click on button.cart-drawer__close
       dismissed it.
     - Focus IS moved into the drawer on open -- activeElement becomes
       the drawer container, which carries tabindex="-1" -- and Tab from
       the last of its 15 controls wraps back inside, so the theme
       already has a working focus trap. Nothing here rebuilds it.
     - Focus is NOT restored on close. After the close button,
       activeElement was <body>: focus falls to the top of the document
       and the keyboard user loses their place.

   That last point is why this block does two things rather than one.
   Escape on its own would make the drawer WORSE for keyboard users --
   a faster way to get dumped at the top of the page. Escape is expected
   dialog behaviour rather than a hard failure, but losing focus on
   close is 2.4.3, so the pair ships together.

   Two deliberate choices:

   The state is read from <body>.cart-drawer-open rather than from
   #cart-drawer.is-open, and the close button is looked up at press
   time. The drawer's contents are replaced on ajax cart updates, so
   anything cached here can go stale; <body> never is.

   The opener is captured BEFORE the drawer opens, on mousedown and on
   Enter/Space, rather than read when it opens. By the time the class
   lands the theme has already moved focus into the drawer, so the
   opener is gone. Recording it early is what makes focus return to the
   right control whether the drawer was opened from the header cart
   link, a product page Add to Cart, or an upsell.

   window.__undtdA11yCartDrawer reports 'active' once attached.
------------------------------------------------------------------ */
(function () {
  'use strict';

  var OPEN_BODY_CLASS = 'cart-drawer-open';
  var DRAWER_ID = 'cart-drawer';
  var CLOSE_SEL = '#' + DRAWER_ID + ' .cart-drawer__close';
  var FALLBACK_OPENER = '.cart-link';

  var opener = null;
  var wasOpen = null;   /* null = first pass, so we never steal focus on load */

  function isOpen() {
    return document.body.classList.contains(OPEN_BODY_CLASS);
  }

  function drawer() {
    return document.getElementById(DRAWER_ID);
  }

  /* ---- remember what opened it ----------------------------------- */

  function remember(e) {
    /* Already open: this is a control INSIDE the drawer (an upsell Add to
       Cart, say), not the thing that opened it. */
    if (isOpen()) return;

    var el = e.target;
    if (!el || !el.closest) return;

    el = el.closest('a[href], button, [tabindex]:not([tabindex^="-"])');
    if (!el) return;

    var d = drawer();
    if (d && d.contains(el)) return;

    opener = el;
  }

  /* ---- Escape ----------------------------------------------------- */

  function onKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      remember(e);
      return;
    }

    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (!isOpen()) return;

    var btn = document.querySelector(CLOSE_SEL);
    if (!btn) return;

    e.preventDefault();
    /* Close through the theme's own button so the animation, the body
       class and whatever else it maintains all stay in sync -- the same
       approach P2-6 takes with #mobile-dropdown. Focus is restored by
       the observer below when the body class drops. */
    btn.click();
  }

  /* ---- focus return ----------------------------------------------- */

  function closed() {
    var d = drawer();
    var active = document.activeElement;

    /* Only reclaim focus if it was lost. If the person has already moved
       somewhere deliberate, leave them there. */
    var lost = !active ||
               active === document.body ||
               active === document.documentElement ||
               (d && d.contains(active));
    if (!lost) return;

    var target = opener;
    if (!target || !document.contains(target)) {
      /* The opener can be gone -- an upsell Add to Cart re-renders the
         drawer's contents. The header cart link is always there and is
         the sensible place to land after closing a cart.
         Both cart openers are <button class="cart-link"> (header and mobile
         nav) and one is always display:none, so take the one that renders.
         The selector was 'a.cart-link' until 2026-10-29 and never matched. */
      target = Array.prototype.filter.call(
        document.querySelectorAll(FALLBACK_OPENER),
        function (el) { return el.offsetParent !== null; }
      )[0] || null;
    }

    opener = null;
    if (!target) return;
    try { target.focus(); } catch (err) {}
  }

  function sync() {
    var open = isOpen();
    if (open === wasOpen) return;
    if (wasOpen !== null && !open) closed();
    wasOpen = open;
  }

  function init() {
    wasOpen = isOpen();

    document.addEventListener('mousedown', remember, true);
    document.addEventListener('keydown', onKeydown, true);

    /* <body> rather than the drawer: the drawer's contents are replaced on
       ajax cart updates, and an observer attached to a replaced node stops
       firing silently. */
    new MutationObserver(sync).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    window.__undtdA11yCartDrawer = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ------------------------------------------------------------------
   N-18 — ambient videos keep decoding after they scroll off screen
   ------------------------------------------------------------------
   NOT A WCAG ITEM, and should not be reported as one. 2.2.2 is already
   satisfied by the stop control P1-2 adds, and no success criterion
   requires pausing off-screen media. This is a battery and CPU fix.

   Measured on the homepage, 2026-10-27: scrolled to 2684px of a 3659px
   document with all FOUR videos out of view, waited, and all four were
   still playing -- timecodes advanced, and the looping ones wrapped past
   their end. The theme starts them on scroll-into-view and never stops
   them, so every homepage visit leaves them decoding for the rest of
   the session.

   Sits in this file rather than the theme's scroll code because the
   pause/resume has to respect what the PERSON chose, and that state
   lives here: data-undtd-intent, set by the P1-2 control. Resuming a
   video someone deliberately stopped would be a 2.2.2 regression, so
   the two pieces belong together.

   Rules, in order of precedence:
     - an explicit stop always wins; an off-screen video that the person
       stopped stays stopped when it scrolls back
     - prefers-reduced-motion never resumes on its own
     - otherwise, resume only what THIS block paused, from the frame it
       paused at -- so scrolling back looks like nothing happened

   It pauses rather than calling P1-2's stop(), which resets to frame 0:
   scrolling past a video should not restart it.

   window.__undtdA11yOffscreen reports 'active', or 'unsupported' where
   IntersectionObserver is missing (in which case nothing changes and the
   videos behave exactly as they do today).
------------------------------------------------------------------ */
(function () {
  'use strict';

  var FLAG = 'data-undtd-offscreen';   /* this block paused it */
  var SEEN = 'data-undtd-io';          /* already being observed */
  var VIS = 'data-undtd-vis';          /* '1' on screen, '0' off screen */
  var INTENT = 'data-undtd-intent';    /* 'play' | 'stop', set by P1-2 */
  var SEL = '.content-card video, .content-feature video, .und-hero video';

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }

  function leave(video) {
    if (video.paused) return;
    video.setAttribute(FLAG, '1');
    try { video.pause(); } catch (e) {}
  }

  function enter(video) {
    /* Only ever resume what this block paused. A video paused by the
       person, or never started by the theme, is left alone. */
    if (!video.hasAttribute(FLAG)) return;
    video.removeAttribute(FLAG);

    if (video.getAttribute(INTENT) === 'stop') return;
    if (reduced() && video.getAttribute(INTENT) !== 'play') return;

    try {
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    } catch (e) {}
  }

  var io = null;

  /* An IntersectionObserver reports the state at observe() time and then only
     on CHANGES. The theme starts these videos itself, after this runs and
     without any intersection changing, so a video that was off screen and
     paused when it was first observed would start playing and never be seen
     again. Measured on the homepage: three of four decoding off screen on a
     fresh load. This listener is what closes that -- anything that starts
     while off screen is pushed straight back down. */
  function onPlay(e) {
    if (e.target.getAttribute(VIS) === '0') leave(e.target);
  }

  function scan() {
    var vids = document.querySelectorAll(SEL);
    for (var i = 0; i < vids.length; i++) {
      if (vids[i].getAttribute(SEEN)) continue;
      vids[i].setAttribute(SEEN, '1');
      vids[i].addEventListener('play', onPlay);
      io.observe(vids[i]);
    }
  }

  function init() {
    if (!window.IntersectionObserver) {
      window.__undtdA11yOffscreen = 'unsupported';
      return;
    }

    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var target = entries[i].target;
        /* Recorded before acting, because onPlay reads it. */
        target.setAttribute(VIS, entries[i].isIntersecting ? '1' : '0');
        if (entries[i].isIntersecting) { enter(target); }
        else { leave(target); }
      }
    }, {
      /* Slack either side so a video is not stopped and started again by
         a few pixels of scroll near the viewport edge. */
      rootMargin: '200px 0px 200px 0px',
      threshold: 0
    });

    scan();

    /* Same reason P1-2 rescans: sections re-render on theme-editor edits
       and on some AJAX paths, bringing new <video> elements with them. */
    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; scan(); }, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.__undtdA11yOffscreen = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* ------------------------------------------------------------------
   N-42 — role="button" controls that do not answer to Space
   ------------------------------------------------------------------
   The product showcase's carousel controls are
   <a href="#" role="button" data-jump-prev|next>. Almost all of that is
   right: the role means they announce as buttons rather than links, the
   name comes from a .visually-hidden span beside an aria-hidden icon,
   they measure 50x43 so 2.5.8 is comfortable, and a REAL Enter press
   advances the carousel without adding a '#' or scrolling the page --
   the theme's own handler calls preventDefault.

   What they do not do is answer to Space. Measured with real key
   presses: the carousel did not move and the PAGE SCROLLED instead,
   485 to 1420, which is the browser default for Space when the focused
   element is not a native button. The ARIA role is what makes this a
   defect rather than a quirk: it promises button behaviour, so someone
   told "Go Forward, button" presses the key a button takes and loses
   their place instead of advancing the carousel.

   theme_undtd.js binds with $("[data-jump-next]", $container).on("click",
   ...) -- by attribute, directly on the element, with no keydown handling
   anywhere near it.

   THE BETTER FIX IS IN THE SECTION, not here: make them real
   <button type="button"> elements. That gets Enter and Space for free and
   drops an href that never meant anything, and it is safe -- the CSS keys
   only on classes (.showcase-controls__nav, .is-hidden, --prev, --next)
   with no element-type anchor selectors, and the JS selects by attribute,
   so neither would notice the swap. It needs the section file and a
   button chrome reset, so it is recorded as the preferred follow-up
   rather than done here.

   This block is the behaviour fix in the meantime, and it generalises:
   any element claiming role="button" that is not a native button gets
   Space, so a div or anchor added later is covered without another pass.

   Deliberately NOT delegated through a readyState guard like the other
   blocks: it listens on document, which always exists, and it resolves
   its target at key time rather than at bind time, so it works for
   controls injected later.

   window.__undtdA11ySpaceActivate reports 'active' once attached.
------------------------------------------------------------------ */
(function () {
  'use strict';

  function onKeydown(e) {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;

    /* Bubble phase, so the element's own handlers get first refusal. If
       something already dealt with this press, leave it alone rather than
       firing the action twice. */
    if (e.defaultPrevented) return;

    var el = (e.target && e.target.closest) ? e.target.closest('[role="button"]') : null;
    if (!el) return;

    /* Native controls already do this correctly -- verified on the P1-2
       video control, where Space toggles exactly once. */
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') return;

    if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return;

    /* preventDefault stops the page scrolling; click() runs whatever the
       theme bound, so the behaviour stays the theme's. */
    e.preventDefault();
    el.click();
  }

  document.addEventListener('keydown', onKeydown, false);
  window.__undtdA11ySpaceActivate = 'active';
})();

/* ------------------------------------------------------------------
   PDP image zoom viewer — keyboard access and dialog behaviour
   (WCAG 2.1.1, 2.1.2, 2.4.3, 4.1.2) · 2026-10-29 code audit
   ------------------------------------------------------------------
   slate.ProductGallery opens .product-single__detail-viewer (a full-
   screen position:fixed overlay, z-index 1000) from a delegated click
   on img.product-single__photo, and slate.DetailViewer closes it from a
   click on any image inside the viewer. Nothing else. From code:

     - the trigger is a bare <img>: no tabindex, no role, so keyboard
       users cannot open zoom at all (2.1.1);
     - the viewer has no role, no name and no close control; Escape does
       nothing, and its only close path is clicking a non-focusable
       image -- a keyboard user who lands in it cannot leave (2.1.2);
     - focus is never moved in, so Tab walks the page hidden behind the
       overlay, and is never restored on close (2.4.3).

   Same approach as P2-6 and N-34: nothing in theme_undtd.js changes.
   The open state is read from body.detail-viewer-open, which the theme
   sets in DetailViewer.open/close, and closing goes through the theme's
   own image-click handler so its fade and body class stay in sync.

   The trigger images get role="button" + tabindex="0" rather than a
   wrapping <button>, so the gallery's markup, Owl Carousel and the
   lazysizes hooks see exactly the DOM they always have. Enter is
   handled here; Space is already covered by the N-42 block, which
   clicks any non-native [role="button"].

   Only sections whose data-image-zoom-type is not "false" are touched
   -- the same switch ProductGallery reads before binding its click.

   window.__undtdA11yZoom reports 'active' once attached, 'no-viewer'
   on templates without one.
------------------------------------------------------------------ */
(function () {
  'use strict';

  var OPEN_BODY_CLASS = 'detail-viewer-open';
  var SECTION_SEL = '[data-image-zoom-type]';
  var TRIGGER_SEL = 'img.product-single__photo';
  var VIEWER_SEL = '.product-single__detail-viewer';
  var CLOSE_CLASS = 'undtd-zoom-close';

  var opener = null;
  var wasOpen = null;   /* null = first pass, so we never steal focus on load */

  function isOpen() {
    return document.body.classList.contains(OPEN_BODY_CLASS);
  }

  function zoomOn(el) {
    var section = el.closest(SECTION_SEL);
    return !!section && section.getAttribute('data-image-zoom-type') !== 'false';
  }

  function isTrigger(el) {
    /* Owl's loop clones are copies for the swipe illusion, not real slides;
       making them tab stops would double every image. */
    return el.matches(TRIGGER_SEL) && !el.closest('.cloned, noscript') && zoomOn(el);
  }

  /* The viewer showing right now. jQuery's fadeIn sets display before the
     theme adds the body class, so by the time the observer runs it is
     the one whose display is not none. */
  function activeViewer() {
    var all = document.querySelectorAll(VIEWER_SEL);
    for (var i = 0; i < all.length; i++) {
      if (window.getComputedStyle(all[i]).display !== 'none') return all[i];
    }
    return null;
  }

  /* ---- triggers --------------------------------------------------- */

  function syncTriggers() {
    var imgs = document.querySelectorAll(TRIGGER_SEL);
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!isTrigger(img)) {
        /* An Owl clone copied from a slide this block already enhanced. */
        if (img.hasAttribute('data-undtd-zoom')) img.setAttribute('tabindex', '-1');
        continue;
      }
      if (!img.hasAttribute('data-undtd-zoom')) {
        img.setAttribute('data-undtd-zoom', '');
        img.setAttribute('role', 'button');
        /* An <img role="button"> takes its name from aria-label, not alt,
           so carry the description across with the action in front. */
        var alt = img.getAttribute('alt') || '';
        img.setAttribute('aria-label', alt ? 'Zoom image: ' + alt : 'Zoom image');
      }
      /* Below the mobile breakpoint the gallery is an Owl carousel and
         off-screen slides are only translated out of view. Only the
         showing slide is a tab stop, or focus would land on images the
         person cannot see (2.4.7); the dots move between slides. */
      var item = img.closest('.owl-item');
      var tabbable = !item || item.classList.contains('active');
      img.setAttribute('tabindex', tabbable ? '0' : '-1');
    }
  }

  var syncQueued = false;
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(function () { syncQueued = false; syncTriggers(); });
  }

  /* ---- dialog semantics and close control ------------------------- */

  function prepareViewer(viewer) {
    if (viewer.hasAttribute('data-undtd-zoom-viewer')) return;
    viewer.setAttribute('data-undtd-zoom-viewer', '');
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');

    var title = viewer.querySelector('.product-single__detail-viewer-title');
    if (title) {
      if (!title.id) title.id = 'undtd-zoom-title-' + Math.random().toString(36).slice(2, 8);
      viewer.setAttribute('aria-labelledby', title.id);
    } else {
      viewer.setAttribute('aria-label', 'Image zoom');
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = CLOSE_CLASS;
    btn.innerHTML =
      '<span class="visually-hidden">Close zoom</span>' +
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">' +
      '<path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" fill="none"/></svg>';
    btn.addEventListener('click', function () { closeViewer(viewer); });
    /* First in the dialog, so it is the first stop and the focus target. */
    viewer.insertBefore(btn, viewer.firstChild);
  }

  function closeViewer(viewer) {
    /* DetailViewer binds its close to a click on any image in the list. */
    var img = viewer.querySelector('.product-single__detail-viewer-list img');
    if (img) img.click();
  }

  function focusables(viewer) {
    return Array.prototype.filter.call(
      viewer.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex^="-"])'),
      function (el) { return el.offsetParent !== null || el.classList.contains(CLOSE_CLASS); }
    );
  }

  /* ---- keys ------------------------------------------------------- */

  function onKeydown(e) {
    if (!isOpen()) {
      var t = e.target;
      if (!t || !t.hasAttribute || !t.hasAttribute('data-undtd-zoom')) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        opener = t;
        t.click();
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        /* N-42 performs the click; just remember where to come back to. */
        opener = t;
      }
      return;
    }

    var viewer = activeViewer();
    if (!viewer) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeViewer(viewer);
      return;
    }

    if (e.key !== 'Tab') return;

    /* The overlay covers the whole viewport, so everything behind it is
       obscured: keep focus inside. */
    var items = focusables(viewer);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var i = items.indexOf(document.activeElement);

    if (i === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && i === 0) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && i === items.length - 1) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Pointer opens: remember the image so focus can come back to it. */
  function onMousedown(e) {
    if (isOpen()) return;
    var el = e.target;
    if (el && el.hasAttribute && el.hasAttribute('data-undtd-zoom')) opener = el;
  }

  /* ---- open / close ----------------------------------------------- */

  function opened() {
    var viewer = activeViewer();
    if (!viewer) return;
    prepareViewer(viewer);
    var btn = viewer.querySelector('.' + CLOSE_CLASS);
    if (btn) btn.focus({ preventScroll: true });
  }

  function closed() {
    var active = document.activeElement;
    var lost = !active || active === document.body ||
               active === document.documentElement ||
               !!(active.closest && active.closest(VIEWER_SEL));
    var target = opener;
    opener = null;
    if (!lost || !target || !document.contains(target)) return;
    try { target.focus({ preventScroll: true }); } catch (err) {}
  }

  function sync() {
    var open = isOpen();
    if (open === wasOpen) return;
    if (wasOpen !== null) { if (open) { opened(); } else { closed(); } }
    wasOpen = open;
  }

  function init() {
    if (!document.querySelector(VIEWER_SEL)) { window.__undtdA11yZoom = 'no-viewer'; return; }

    syncTriggers();
    /* Owl builds and tears down its stage at the mobile breakpoint and
       moves .active between slides; colour changes re-filter the gallery.
       Scoped to the product sections, and this block only ever writes
       role/tabindex/aria-label, so it cannot retrigger itself. */
    var sections = document.querySelectorAll(SECTION_SEL);
    for (var i = 0; i < sections.length; i++) {
      new MutationObserver(queueSync).observe(sections[i], {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    }

    wasOpen = isOpen();
    new MutationObserver(sync).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    document.addEventListener('mousedown', onMousedown, true);
    document.addEventListener('keydown', onKeydown, true);

    window.__undtdA11yZoom = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ------------------------------------------------------------------
   Cart drawer — removals lose focus, and updates are silent
   (WCAG 2.4.3, 4.1.3) · 2026-10-29 code audit
   ------------------------------------------------------------------
   theme.CartDrawer.onCartUpdate animates a removed line to height 0 and
   then $item.remove()s it -- with the Remove button the person just
   pressed still focused, so focus falls to <body>. "Clear All" does the
   same, and an upsell Add to Cart destroys its own card. Nothing in the
   drawer is a live region: the new item count and total are written with
   .text()/.html() and never announced.

   Same approach as N-34: theme_undtd.js is untouched. This block listens
   for the CartAPI events the drawer itself listens for, and watches the
   drawer's DOM for the moment the focused control disappears.

   Where focus goes, in order:
     1. after a Remove: the next line's Remove button, else the previous
        line's (resolved by line id at focus time, because the theme swaps
        each surviving line's [data-contents] on every update);
     2. cart now empty: the visible "continue browsing" button;
     3. anything else lost while the drawer is open: the close button.

   The status region sits inside #cart-drawer, so it is never announced
   while the drawer is closed (it is inert then) and is inside the dialog
   while it is open. The drawer element itself is never replaced, so the
   region survives every ajax update.

   window.__undtdA11yCartStatus reports 'active' once attached.
------------------------------------------------------------------ */
(function () {
  'use strict';

  var DRAWER_ID = 'cart-drawer';
  var ITEM_SEL = '[data-cart-item]';
  var REMOVE_SEL = '[data-item-remove]';

  var drawer = null;
  var region = null;
  var lastCount = null;
  var pending = null;      /* { next: id|null, prev: id|null } while a Remove is in flight */
  var pendingTimer = null;
  var announceTimer = null;

  function isOpen() {
    return document.body.classList.contains('cart-drawer-open');
  }

  function focusLost() {
    var a = document.activeElement;
    return !a || a === document.body || a === document.documentElement || !a.isConnected ||
           !!(a.closest && a.closest('.is-removing'));
  }

  function removeButtonFor(id) {
    if (id == null) return null;
    var item = drawer.querySelector(ITEM_SEL + '[data-id="' + id + '"]');
    if (!item || item.classList.contains('is-removing')) return null;
    return item.querySelector(REMOVE_SEL);
  }

  function fallbackTarget() {
    if (drawer.classList.contains('is-empty')) {
      return drawer.querySelector('.ajax-cart__empty button');
    }
    return drawer.querySelector('.cart-drawer__close');
  }

  function restoreFocus() {
    if (!isOpen() || !focusLost()) return;
    var target = null;
    if (pending) target = removeButtonFor(pending.next) || removeButtonFor(pending.prev);
    if (!target) target = fallbackTarget();
    if (target) { try { target.focus(); } catch (err) {} }
  }

  /* ---- remember the neighbours of the line being removed ---------- */

  function onClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest(REMOVE_SEL) : null;
    if (!btn || !drawer.contains(btn)) return;
    var item = btn.closest(ITEM_SEL);
    if (!item) return;
    var items = Array.prototype.slice.call(drawer.querySelectorAll(ITEM_SEL));
    var i = items.indexOf(item);
    var next = items[i + 1], prev = items[i - 1];
    pending = {
      next: next ? next.getAttribute('data-id') : null,
      prev: prev ? prev.getAttribute('data-id') : null
    };
    clearTimeout(pendingTimer);
    /* The theme's removal animation is 300ms; give the request time too. */
    pendingTimer = setTimeout(function () { pending = null; }, 5000);
  }

  /* ---- announce ---------------------------------------------------- */

  function announce(text) {
    /* Clear, then set on a later tick: identical consecutive messages are
       otherwise swallowed, and the drawer may only just have lost inert. */
    region.textContent = '';
    clearTimeout(announceTimer);
    announceTimer = setTimeout(function () { region.textContent = text; }, 150);
  }

  function onUpdate(e) {
    var cart = e.detail && e.detail.cart;
    if (!cart) return;

    var count = cart.item_count;
    var summary = count + (count === 1 ? ' item' : ' items') +
      (cart.items_subtotal_price_formatted ? ', total ' + cart.items_subtotal_price_formatted : '');

    var msg;
    if (count === 0) msg = 'Your cart is empty.';
    else if (lastCount === null || count === lastCount) msg = 'Cart updated. ' + summary + '.';
    else if (count < lastCount) msg = 'Item removed. Cart: ' + summary + '.';
    else msg = 'Added to cart. Cart: ' + summary + '.';

    lastCount = count;
    announce(msg);

    /* Covers the case where nothing animates out (an upsell re-render,
       or the drawer swapping a line's contents under the focused node). */
    setTimeout(restoreFocus, 0);
  }

  function init() {
    drawer = document.getElementById(DRAWER_ID);
    if (!drawer) { window.__undtdA11yCartStatus = 'no-drawer'; return; }

    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    drawer.appendChild(region);

    var label = drawer.querySelector('[data-cart-items-label-text]');
    var n = label ? parseInt(label.textContent, 10) : NaN;
    lastCount = isNaN(n) ? null : n;

    drawer.addEventListener('click', onClick, true);
    window.addEventListener('cartAPI.update', onUpdate);

    /* The focused Remove button disappears when the theme's animation
       completes; catch that moment rather than guessing a delay. */
    new MutationObserver(function () {
      if (focusLost()) restoreFocus();
    }).observe(drawer, { childList: true, subtree: true });

    window.__undtdA11yCartStatus = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ------------------------------------------------------------------
   Infinite scroll (Ajaxinate) — focus lost on load, loads are silent
   (WCAG 2.4.3, 4.1.3) · 2026-10-29 code audit
   ------------------------------------------------------------------
   Collection, brands, league-team and search pages run
   `new Ajaxinate()` (scroll method). Tabbing to the pagination link
   scrolls it into view, which triggers the load; Ajaxinate then appends
   the next page to #AjaxinateLoop and REPLACES #AjaxinatePagination's
   innerHTML -- destroying the link that has focus. Focus falls to
   <body>, and nothing says that products were added.

   Infinite scroll itself is kept: switching to Ajaxinate's click method
   is a UX decision for the client, not a conformance requirement. What
   this block does instead:
     - if focus was on the pagination link when it was replaced, move it
       to the first product that just loaded, so Tab continues through
       the new results instead of restarting at the top of the page;
     - announce "N more products loaded" in a polite status region.

   Mouse and touch scrolling are unaffected: focus is only moved when it
   was on the replaced link.

   window.__undtdA11yInfinite reports 'active' / 'no-pagination'.
------------------------------------------------------------------ */
(function () {
  'use strict';

  function init() {
    var loop = document.getElementById('AjaxinateLoop');
    var pagination = document.getElementById('AjaxinatePagination');
    if (!loop || !pagination) { window.__undtdA11yInfinite = 'no-pagination'; return; }

    var region = document.createElement('p');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    pagination.parentNode.insertBefore(region, pagination.nextSibling);

    /* Set on every focusin; when the link is destroyed no event fires, so
       it still says where focus was at the moment of the swap. */
    var focusWasInPagination = false;
    document.addEventListener('focusin', function (e) {
      focusWasInPagination = pagination.contains(e.target);
    });

    var added = [];
    function collect(records) {
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes, function (n) {
          if (n.nodeType === 1) added.push(n);
        });
      });
    }

    var loopObserver = new MutationObserver(collect);
    loopObserver.observe(loop, { childList: true });

    new MutationObserver(function () {
      /* Ajaxinate appends to the loop before swapping the pagination, but
         the two observers' callbacks can run in either order: flush. */
      collect(loopObserver.takeRecords());
      if (!added.length) return;

      var links = [];
      added.forEach(function (n) {
        var a = n.matches('a[href]') ? n : n.querySelector('a[href]:not([tabindex^="-"])');
        if (a) links.push(a);
      });
      added = [];
      if (!links.length) return;

      region.textContent = links.length + (links.length === 1 ? ' more product loaded' : ' more products loaded');

      var a = document.activeElement;
      var lost = !a || a === document.body || !a.isConnected;
      if (focusWasInPagination && lost) {
        focusWasInPagination = false;
        try { links[0].focus(); } catch (err) {}
      }
    }).observe(pagination, { childList: true, subtree: true });

    window.__undtdA11yInfinite = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ------------------------------------------------------------------
   Server-rendered form results — focus the error summary
   (WCAG 3.3.1, 4.1.3) · 2026-10-29 code audit
   ------------------------------------------------------------------
   Login, recover, register, reset, activate and contact forms post to
   Shopify and come back as a full page with {{ form.errors |
   default_errors }} at the top of the form. Nothing pointed anyone at it:
   screen-reader users started over at the top of the page with no hint
   that the submission failed.

   The templates now wrap that output in .form-errors[tabindex="-1"]
   (and give the contact success note an id), and each failed field
   carries aria-invalid + aria-describedby pointing back at it. This
   block focuses the first VISIBLE one after load, which also makes
   screen readers read it out.

   Runs a tick after DOMContentLoaded because timber.loginForms() (jQuery
   ready) is what un-hides the recover form for #recover; checking any
   earlier would see it hidden and skip its errors.
------------------------------------------------------------------ */
(function () {
  'use strict';

  function run() {
    var nodes = document.querySelectorAll('.form-errors[tabindex="-1"], #ContactFormSuccess');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null) {
        try { nodes[i].focus(); } catch (err) {}
        return;
      }
    }
  }

  function init() { setTimeout(run, 0); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ------------------------------------------------------------------
   Desktop mega-menu — Escape dismisses       (WCAG 1.4.13) · 2026-10-29
   ------------------------------------------------------------------
   timber.accessibleNav opens a dropdown on mouseenter (.nav-hover on the
   <li>) or when a link inside it takes focus (.nav-focus on the parent
   link; CSS shows `.nav-focus + .site-nav__dropdown`). It has no keydown
   handling at all, so a dropdown covering the page could only be
   dismissed by moving the pointer or focus away.

   Escape closes every open dropdown and leaves focus on the parent link.
   Focusing that link re-runs the theme's focus handler, which re-adds
   .nav-focus synchronously -- so the classes are cleared AFTER focus()
   returns. Tabbing on into the submenu reopens it, as the theme intends.
------------------------------------------------------------------ */
(function () {
  'use strict';

  function onKeydown(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var nav = document.getElementById('AccessibleNav');
    if (!nav) return;

    var open = nav.querySelectorAll('.nav-hover, .nav-focus');
    if (!open.length) return;

    var active = document.activeElement;
    var parentLink = null;
    if (active && nav.contains(active)) {
      var item = active.closest('.site-nav--has-dropdown:not(.site-nav--has-dropdown-grandchild)');
      parentLink = item ? item.querySelector(':scope > a') : null;
    }

    e.preventDefault();
    if (parentLink && parentLink !== active) parentLink.focus();

    Array.prototype.forEach.call(nav.querySelectorAll('.nav-hover, .nav-focus'), function (el) {
      el.classList.remove('nav-hover', 'nav-focus');
    });
    Array.prototype.forEach.call(nav.querySelectorAll('.site-nav--has-dropdown > a[aria-expanded]'), function (a) {
      a.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('keydown', onKeydown, false);
  window.__undtdA11yMegaNav = 'active';
})();


/* ------------------------------------------------------------------
   Predictive search — state, announcements, Escape, focus order
   (WCAG 4.1.2, 4.1.3, 1.4.13, 2.4.3) · 2026-10-29 code audit
   ------------------------------------------------------------------
   The SEARCH AUTOCOMPLETE block in theme_undtd.js slides #banana_Search
   open on input focus and fills #product-search-results plus the
   "Products (N)" count. From code:
     - the input never says it controls a panel or whether it is open;
     - the count changes silently;
     - the panel only closes on an outside click, or blur with no term;
     - #banana_Search sits in the DOM after the search submit, the country
       switcher and the cart button, so Tab from the input reached three
       unrelated controls before the results shown right beneath it.

   This block, without touching the theme's module:
     - aria-controls / aria-expanded on the input, kept in step with the
       panel's .active class;
     - a polite status region outside the panel (the panel is display:none
       while closed, and a hidden region is not announced), updated 600ms
       after typing settles so each keystroke is not read out;
     - Escape closes through the theme's own outside-click handler (a click
       on <body> is "outside") and returns focus to the input;
     - Tab from the input moves into the results; Tab past the last result
       continues to the search submit; Shift+Tab mirrors both.
------------------------------------------------------------------ */
(function () {
  'use strict';

  function init() {
    var wrap = document.querySelector('.search-bar-wrappa');
    var panel = document.getElementById('banana_Search');
    var input = wrap && wrap.querySelector('input[name="q"]');
    var count = panel && panel.querySelector('.products_number');
    var results = panel && panel.querySelector('#product-search-results');
    if (!wrap || !panel || !input || !count || !results) {
      window.__undtdA11ySearch = 'no-search';
      return;
    }

    var submit = wrap.querySelector('button[type="submit"], input[type="submit"]');

    input.setAttribute('aria-controls', 'banana_Search');
    input.setAttribute('aria-expanded', 'false');

    function isOpen() { return panel.classList.contains('active'); }

    new MutationObserver(function () {
      input.setAttribute('aria-expanded', isOpen() ? 'true' : 'false');
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });

    /* ---- announce the count ---------------------------------------- */

    var region = document.createElement('p');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    wrap.appendChild(region);

    var timer = null;
    function announce() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var term = input.value.trim();
        if (term.length < 2) { region.textContent = ''; return; }
        var n = parseInt(count.textContent, 10) || 0;
        region.textContent = n === 0 ? 'No products found'
          : n + (n === 1 ? ' product found' : ' products found');
      }, 600);
    }
    new MutationObserver(announce).observe(count, { childList: true, characterData: true, subtree: true });

    /* ---- keys ------------------------------------------------------- */

    function resultLinks() {
      return Array.prototype.slice.call(results.querySelectorAll('a[href]'));
    }

    function onKeydown(e) {
      var t = e.target;
      var inPanel = panel.contains(t);

      if ((e.key === 'Escape' || e.key === 'Esc') && isOpen() && (t === input || inPanel)) {
        e.preventDefault();
        document.body.click();
        input.focus();
        return;
      }

      if (e.key !== 'Tab' || !isOpen()) return;
      var links = resultLinks();
      if (!links.length) return;

      if (t === input && !e.shiftKey) {
        e.preventDefault();
        links[0].focus();
      } else if (inPanel && !e.shiftKey && t === links[links.length - 1] && submit) {
        e.preventDefault();
        submit.focus();
      } else if (inPanel && e.shiftKey && t === links[0]) {
        e.preventDefault();
        input.focus();
      } else if (t === submit && e.shiftKey) {
        e.preventDefault();
        links[links.length - 1].focus();
      }
    }

    document.addEventListener('keydown', onKeydown, true);
    window.__undtdA11ySearch = 'active';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ------------------------------------------------------------------
   Product form — variant, price and button state are silent
   (WCAG 4.1.3) · 2026-10-29 code audit
   ------------------------------------------------------------------
   slate.ProductForm rewrites the add-to-cart label ("Select an option",
   "Sold Out", "Add To Cart", "Adding To Cart...") and the price block on
   every option change, with no live region anywhere on the page. A
   screen-reader user picking a size heard the radio become checked and
   nothing about the price, or that the size was sold out.

   One polite status region per product form. It watches the price
   container and the button label, and after changes settle (250ms -- a
   variant change rewrites several nodes) reads out the visible price
   text plus the button state, e.g. "Sale price $80 Regular price $100.
   Sold Out". Nodes the theme marks aria-hidden or hides are skipped, so
   the message matches what is on screen.

   Silent until the person interacts with that form: the theme's first
   availability pass runs on load and must not be read out.
------------------------------------------------------------------ */
(function () {
  'use strict';

  function visibleText(root) {
    var out = [];
    (function walk(node) {
      if (node.nodeType === 3) { out.push(node.nodeValue); return; }
      if (node.nodeType !== 1) return;
      if (node.getAttribute('aria-hidden') === 'true') return;
      if (window.getComputedStyle(node).display === 'none') return;
      for (var c = node.firstChild; c; c = c.nextSibling) walk(c);
    })(root);
    return out.join(' ').replace(/\s+/g, ' ').trim();
  }

  function attach(form) {
    var btnText = form.querySelector('.btn--add-to-cart .btn__text');
    var section = form.closest('[data-section-id]') || form;
    var price = section.querySelector('[data-price-container]');
    if (!btnText) return;

    var region = document.createElement('p');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    form.appendChild(region);

    var engaged = false;
    ['change', 'click', 'keydown'].forEach(function (type) {
      section.addEventListener(type, function () { engaged = true; }, true);
    });

    var timer = null, last = '';
    function update() {
      if (!engaged) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        var parts = [];
        if (price) parts.push(visibleText(price));
        parts.push(visibleText(btnText));
        var msg = parts.filter(Boolean).join('. ');
        if (msg && msg !== last) { last = msg; region.textContent = msg; }
      }, 250);
    }

    var opts = { childList: true, characterData: true, subtree: true, attributes: true,
                 attributeFilter: ['aria-hidden', 'class'] };
    new MutationObserver(update).observe(btnText, opts);
    if (price) new MutationObserver(update).observe(price, opts);
  }

  function init() {
    var forms = document.querySelectorAll('[data-section-type="product-template"] form[action="/cart/add"]');
    Array.prototype.forEach.call(forms, attach);
    window.__undtdA11yProductStatus = forms.length ? 'active' : 'no-form';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
